import React, { useEffect, useRef, useState } from 'react';
import './ChangeName.scss';
import { useTranslation } from 'react-i18next';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { brightDataCountries, brightDataCountriesWithoutGlobal, isAdminOrOwner } from '../../../utils/utils';
import { useAccount } from '../../../contexts/account/AccountContexts';
import DropdownItem from '../../dropdown-item/DropdownItem';
import { useModuleAccess } from '../../../hooks/global/UseModuleAccess';
import MonitoringScheduleSelect from '../monitoring-schedule-select/MonitoringScheduleSelect';
import { NavLink } from 'react-router-dom';
import useAddon from '../../../hooks/global/UseAddon';
import AllocationControl from '../../allocation-control/AllocationControl';
import { PROJECTS } from '../../../routes/path';
import {
  MONITORING_SCHEDULE_PRESETS,
  MonitoringInterval,
  MonitoringSchedulePreset,
  presetOfInterval,
} from '../../../models/projects/MonitoringInterval';

interface ChangeNameProps {
  projectId: number;
  selectedProjectLabel: string;
  selectedCountry: string | null;
  selectedMonitoringInterval?: MonitoringInterval;
  onChangeProjectLabel: (projectLabel: string) => void;
  onChangeProjectCountry: (country: string | null) => void;
  onChangeMonitoringInterval: (interval: MonitoringInterval) => void;
  /** Agency Hub accounts: the project's new reservation (reported only when it differs). */
  onChangeAllocation: (allocation: number | null) => void;
  isSaveDisabled: (disabled: boolean) => void;
}

const ChangeName: React.FC<ChangeNameProps> = ({
  projectId,
  selectedProjectLabel,
  selectedCountry,
  selectedMonitoringInterval,
  onChangeProjectLabel,
  onChangeProjectCountry,
  onChangeMonitoringInterval,
  onChangeAllocation,
  isSaveDisabled,
}) => {
  const { t } = useTranslation();
  const { accountResponse, projectResponse } = useAccount();

  // The Agency Hub's allocation (OWNER / ADMIN only). The floor is what the project uses; a paused
  // project holds 0 by rule and shows the field disabled. The ceiling is what the project takes from
  // the pool now plus what the pool still has — its own share is the pool it can reuse. An empty field
  // is no limit: the project then takes only the prompts it uses.
  const hasAgencyHub = useAddon('AGENCY_HUB');
  const project = projectResponse?.find((candidate) => candidate.id === projectId);
  const canAllocate = hasAgencyHub && !!project && !!accountResponse && isAdminOrOwner(accountResponse.role);
  const used = (project?.monitorStatusCounts?.active ?? 0) + (project?.monitorStatusCounts?.pending ?? 0);
  const isPaused = project?.status === 'PAUSED';
  const currentAllocation = isPaused ? 0 : Math.max(project?.promptAllocation ?? used, used);
  const currentLimit: number | null = isPaused || project?.promptAllocation !== null ? currentAllocation : null;
  const freePool = accountResponse
    ? Math.max(0, accountResponse.maxMonitorCount - accountResponse.allocatedMonitorCount)
    : 0;
  const [allocation, setAllocation] = useState<number | null>(currentLimit);
  useEffect(() => {
    setAllocation(currentLimit);
  }, [currentLimit, projectId]);
  // Entitlement addon: support (full super admin) may set a client's schedule before they buy it.
  const scheduleAccess = useModuleAccess('MANAGE_MONITORING_SCHEDULE', projectId, { allowSuperAdmin: true });
  const [country, setCountry] = useState<string | null>(selectedCountry);
  const [schedulePreset, setSchedulePreset] = useState<MonitoringSchedulePreset>(
    presetOfInterval(selectedMonitoringInterval)
  );
  const [isRequiredError, setRequiredError] = useState<boolean>(true);
  const projectName = useRef<HTMLInputElement | null>(null);
  // A ref, not a plain variable: a variable reset to `true` on every render, so the "required" hint vanished
  // whenever anything re-rendered the dialog.
  const isFirstLoad = useRef(true);

  useEffect(() => {
    setCountry(selectedCountry);
  }, [selectedCountry]);

  useEffect(() => {
    setSchedulePreset(presetOfInterval(selectedMonitoringInterval));
  }, [selectedMonitoringInterval]);

  useEffect(() => {
    if (projectName.current) {
      projectName.current.value = selectedProjectLabel;
    }
    setRequiredError(true);
    isFirstLoad.current = true;
  }, [selectedProjectLabel, selectedCountry]);

  useEffect(() => {
    isSaveDisabled(isRequiredError);
  }, [isRequiredError]);

  // Save enables when ANY field differs from the saved project. Decided from the values the handler is given,
  // never from a flag computed during render: the name input is uncontrolled, so typing does not re-render,
  // and a render-time flag froze at `false` once a hook (`useModuleAccess` → `useGetProject`) re-rendered the
  // dialog after mount — Save never enabled for a renamed project (prod, 2026-11-02).
  const isChanged = ({
    label = projectName.current?.value ?? '',
    nextCountry = country,
    nextPreset = schedulePreset,
    nextAllocation = allocation,
  }: {
    label?: string;
    nextCountry?: string | null;
    nextPreset?: MonitoringSchedulePreset;
    nextAllocation?: number | null;
  } = {}): boolean =>
    selectedProjectLabel.toLowerCase().trim() !== label.toLowerCase().trim() ||
    nextCountry !== selectedCountry ||
    nextPreset !== presetOfInterval(selectedMonitoringInterval) ||
    (canAllocate && !isPaused && nextAllocation !== currentLimit);

  const isInvalidWith = (changes: Parameters<typeof isChanged>[0]): boolean =>
    (projectName.current?.value ?? '').trim() === '' || !isChanged(changes);

  const handleOnChange = async () => {
    isFirstLoad.current = false;
    setRequiredError(isInvalidWith({}));
    // Always, not only when valid: otherwise typing a new name and then typing the original back left the
    // parent holding the "new" one, and a later country/schedule change would have saved it. The parent falls
    // back to the current label when the field is empty.
    onChangeProjectLabel(projectName.current?.value ?? '');
  };

  const handleOnChangeCountry = async (code: string | null) => {
    onChangeProjectCountry(code);
    setCountry(code);
    setRequiredError(isInvalidWith({ nextCountry: code }));
  };

  const handleOnChangeSchedule = (preset: MonitoringSchedulePreset) => {
    setSchedulePreset(preset);
    onChangeMonitoringInterval(MONITORING_SCHEDULE_PRESETS[preset]);
    setRequiredError(isInvalidWith({ nextPreset: preset }));
  };

  const handleOnChangeAllocation = (next: number | null) => {
    setAllocation(next);
    onChangeAllocation(next);
    setRequiredError(isInvalidWith({ nextAllocation: next }));
  };

  return (
    <div className="change-name-dialog">
      <div className="change-name-dialog__input">
        <div className="form-box__label">{t('projectName')}:</div>
        <InputText
          ref={projectName}
          type="text"
          id="projectName"
          defaultValue={selectedProjectLabel}
          onInput={handleOnChange}
          className={isRequiredError && !isFirstLoad.current ? 'form-box__invalid' : ''}
        />
        {accountResponse?.addons.includes('GEO') && (
          <>
            <div className="form-box__label">{t('geolocation')}:</div>
            <Dropdown
              value={brightDataCountriesWithoutGlobal.find((option) => option.code === country) || null}
              onChange={(e) => handleOnChangeCountry(e.value.code || null)}
              placeholder={t('selectCountry')}
              options={brightDataCountriesWithoutGlobal}
              className="dropdown"
              optionLabel="name"
              itemTemplate={(option) => <DropdownItem icon={option.icon} name={option.name} />}
              // Global wypadł z listy, więc PrimeReact nie znajdzie go jako zaznaczonej opcji —
              // etykietę projektu, który ma go ustawionego historycznie, dobieramy z pełnej listy.
              valueTemplate={(option) => {
                const current = option ?? brightDataCountries.find((c) => c.code === country);

                return current ? (
                  <DropdownItem icon={current.icon} name={current.name} />
                ) : (
                  <DropdownItem name={t('selectCountry')} />
                );
              }}
              filter
            />
          </>
        )}
        {scheduleAccess.canAccess && (
          <MonitoringScheduleSelect value={schedulePreset} onChange={handleOnChangeSchedule} />
        )}
        {canAllocate && (
          <div className="change-name-dialog__allocation">
            <div className="form-box__label">{t('promptAllocation')}:</div>
            {isPaused ? (
              <div className="change-name-dialog__hint">{t('promptAllocationPaused')}</div>
            ) : (
              <>
                <AllocationControl
                  value={allocation}
                  min={used}
                  max={currentAllocation + freePool}
                  free={Math.max(0, currentAllocation + freePool - (allocation ?? used))}
                  onChange={handleOnChangeAllocation}
                />
                {/* A new tab: the dialog's unsaved name / country / allocation would be lost otherwise. */}
                <NavLink
                  className="change-name-dialog__hint"
                  to={`${PROJECTS}/${projectId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('promptAllocationLowerHint')}
                </NavLink>
              </>
            )}
          </div>
        )}

        {isRequiredError && !isFirstLoad.current && <span>{t('fieldIsRequired')}</span>}
      </div>
    </div>
  );
};

export default ChangeName;
